import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "filterRow", "scrollWrapper"]
  static values = {
    formId: String,
    debounceDelay: { type: Number, default: 200 },
    statusDelay: { type: Number, default: 120 }
  }

  connect() {
    this.debounceTimeout = null
    this.statusTimeout = null
    this._headerResizeObserver = null
    this._longTaskObserver = null

    this._observeLongTasks()

    this._timed("connect:restoreScrollPosition", () => this.restoreScrollPosition())
    this._timed("connect:restoreFocus", () => this.restoreFocus())
    this._timed("connect:hideStatus", () => this.hideStatus())
    this._timed("connect:observeHeaderResize", () => this._observeHeaderResize())
    this._timed("connect:initFilterRowVisibility", () => this.initFilterRowVisibility())
    this._timed("connect:addTopFrameToBodyLinks", () => this.addTopFrameToBodyLinks())

    // Defer all layout reads to a single rAF so they happen after the browser
    // has finished its first layout pass. Reading offsetHeight/offsetWidth
    // synchronously in connect() forces the browser to flush pending layout
    // immediately ("forced reflow"), which can take 90ms+ on complex pages.
    // _initialLayoutPass() batches ALL reads before ANY writes, which is the
    // safest pattern for avoiding cascading reflows.
    requestAnimationFrame(() => this._initialLayoutPass())
  }

  // Links inside a <turbo-frame> target the frame by default. Cell links (e.g.
  // to a record's show page) should navigate the full page instead. Sort and
  // filter links live in <thead>, so they are unaffected.
  //
  // Rather than walking every <a> in the tbody on connect (expensive for large
  // tables with many link-heavy columns), we use a single delegated click
  // listener on the tbody itself. When a link fires a click, we check whether
  // it already carries an explicit data-turbo-frame attribute. If not, we set
  // it to "_top" right at that moment — one DOM write per user action instead
  // of N DOM writes per page load.
  addTopFrameToBodyLinks() {
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return

    // Guard: only attach one listener even if connect() is called multiple times.
    if (tbody._dtTopFrameListener) return

    const handler = (event) => {
      const link = event.target.closest("a[href]")
      if (!link || link.dataset.turboFrame) return
      link.dataset.turboFrame = "_top"
    }

    tbody.addEventListener("click", handler, true)
    tbody._dtTopFrameListener = handler
  }

  disconnect() {
    this.clearPendingSubmit()
    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect()
      this._headerResizeObserver = null
    }
    if (this._longTaskObserver) {
      this._longTaskObserver.disconnect()
      this._longTaskObserver = null
    }
    const tbody = this.element.querySelector("tbody")
    if (tbody?._dtTopFrameListener) {
      tbody.removeEventListener("click", tbody._dtTopFrameListener, true)
      delete tbody._dtTopFrameListener
    }
  }

  // Toggle the filter row open/closed and persist the state.
  toggleFilters() {
    if (!this.hasFilterRowTarget) return

    const currentlyHidden = this.filterRowTarget.style.display === "none"
    this.setFilterRowVisible(currentlyHidden)
  }

  // Reset all filters: navigate to the current path keeping only sort/direction.
  resetFilters() {
    const form = document.getElementById(this.formIdValue)
    if (!form) return

    const params = new URLSearchParams()
    const sortField = form.querySelector("[name='sort']")
    const directionField = form.querySelector("[name='direction']")
    if (sortField?.value) params.set("sort", sortField.value)
    if (directionField?.value) params.set("direction", directionField.value)

    const url = `${window.location.pathname}?${params.toString()}`
    const frame = this.element.closest("turbo-frame")
    if (frame && window.Turbo) {
      window.Turbo.visit(url, { frame: frame.id })
    } else {
      window.location.href = url
    }
  }

  // On connect, open the filter row if the user had it open, or if any filter is active.
  initFilterRowVisibility() {
    if (!this.hasFilterRowTarget) return

    const stored = sessionStorage.getItem(this.filterRowStorageKey)
    const shouldShow = stored === "open" || (stored === null && this.hasActiveFilters())
    this.setFilterRowVisible(shouldShow)
  }

  setFilterRowVisible(visible) {
    if (!this.hasFilterRowTarget) return

    this._timed("setFilterRowVisible", () => {
      // display:none removes the row from the render tree entirely, avoiding the
      // sticky-position recalculations that visibility:collapse triggered for every
      // filter-row <th>. Column widths are kept stable by _lockColumnWidths().
      this.filterRowTarget.style.display = visible ? "" : "none"
      if (visible) {
        this.filterRowTarget.removeAttribute("aria-hidden")
      } else {
        this.filterRowTarget.setAttribute("aria-hidden", "true")
      }

      sessionStorage.setItem(this.filterRowStorageKey, visible ? "open" : "closed")

      const btn = this.element.querySelector("[data-filter-toggle-btn]")
      if (btn) btn.setAttribute("aria-expanded", String(visible))

      // When inside a scroll wrapper, shift the dividing shadow to the bottom
      // of whichever sticky row is now last-visible.
      if (this.hasScrollWrapperTarget) {
        this.scrollWrapperTarget.classList.toggle(
          "scrolled-table-container--filters-open",
          visible
        )
      }
    })
  }

  // Single rAF callback that reads layout properties after the browser has
  // finished its first layout pass, then writes the result.
  //
  // With table-layout: fixed, column widths are determined entirely by the
  // header <th> widths specified in the HTML — content and filter inputs never
  // affect them. There is no need to measure or lock column widths in JS.
  //
  // The only remaining layout read we need is the header row height, used to
  // pin the sticky filter row at exactly the right offset via --dt-header-height.
  // This read is deferred to a rAF so it happens after the first paint, avoiding
  // a forced synchronous reflow in connect().
  //
  //   Reads:  headerRow.offsetHeight  (for --dt-header-height CSS var)
  //   Writes: scrollWrapper CSS custom property
  _initialLayoutPass() {
    if (!this.hasScrollWrapperTarget || !this.hasFilterRowTarget) return

    const headerRow = this.scrollWrapperTarget.querySelector("thead tr:first-child")
    if (!headerRow) return

    const headerHeight = headerRow.offsetHeight
    this.scrollWrapperTarget.style.setProperty("--dt-header-height", `${headerHeight}px`)
  }

  // Watch the header row for actual size changes (window resize, zoom, font
  // changes) and recalculate only when necessary. This replaces the previous
  // approach of calling updateStickyFilterTop() on every filter toggle.
  //
  // IMPORTANT: the callback reads height from the ResizeObserver entry's
  // borderBoxSize rather than reading headerRow.offsetHeight directly.
  // Reading offsetHeight inside a ResizeObserver callback forces a synchronous
  // layout ("forced reflow") because the pending layout from whatever triggered
  // the resize hasn't been committed yet. borderBoxSize is provided by the
  // browser as part of the resize notification itself — no extra layout pass
  // needed. This eliminates the forced-reflow violation that appeared on pages
  // where toggling the filter row's visibility changes column widths (e.g.
  // when a date-range filter input is wider than its header cell).
  _observeHeaderResize() {
    if (!this.hasScrollWrapperTarget || !window.ResizeObserver) return

    const headerRow = this.scrollWrapperTarget.querySelector("thead tr:first-child")
    if (!headerRow) return

    this._headerResizeObserver = new ResizeObserver((entries) => {
      if (!this.hasScrollWrapperTarget) return
      for (const entry of entries) {
        // borderBoxSize includes borders, matching offsetHeight semantics.
        // Fall back to contentRect.height for Firefox < 92 / Safari < 15.4.
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
        this.scrollWrapperTarget.style.setProperty("--dt-header-height", `${height}px`)
      }
    })
    this._headerResizeObserver.observe(headerRow)
  }

  // Returns true if any filter input currently carries a non-empty value.
  hasActiveFilters() {
    const inputs = Array.from(document.querySelectorAll(`[form="${this.formIdValue}"]`))
    return inputs.some((input) => input.value && input.value.length > 0)
  }

  get filterRowStorageKey() {
    return `data-table-filters:${this.formIdValue}:filter-row`
  }

  // Submit when the user presses Enter in a text filter field, then restore focus.
  submitOnEnter(event) {
    if (event.key !== "Enter") return

    // Prevent the browser's own form submission so Turbo/requestSubmit handles it.
    event.preventDefault()

    this.clearPendingSubmit()
    this.storeFocus(event.target)
    this.storeScrollPosition()
    this.scheduleStatus()
    this.submitForm()
  }

  submitNow() {
    this.clearPendingSubmit()
    this.clearStoredFocus()
    this.storeScrollPosition()
    this.scheduleStatus()
    this.submitForm()
  }

  submitDebounced(event) {
    this.clearPendingSubmit()
    this.storeFocus(event.target)
    this.storeScrollPosition()
    this.scheduleStatus()
    this.debounceTimeout = setTimeout(() => this.submitForm(), this.debounceDelayValue)
  }

  clearPendingSubmit() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }

    if (!this.statusTimeout) return

    clearTimeout(this.statusTimeout)
    this.statusTimeout = null
  }

  scheduleStatus() {
    if (!this.hasStatusTarget) return

    this.statusTimeout = setTimeout(() => {
      this.showStatus()
      this.statusTimeout = null
    }, this.statusDelayValue)
  }

  showStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = "Filtering..."
    this.statusTarget.classList.add("is-active")
  }

  hideStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("is-active")
  }

  submitForm() {
    const form = document.getElementById(this.formIdValue)
    if (!form) {
      this.hideStatus()
      return
    }

    if (form.requestSubmit) {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  restoreFocus() {
    const storedFocus = sessionStorage.getItem(this.focusStorageKey)
    if (!storedFocus) return

    this.clearStoredFocus()

    let focusState
    try {
      focusState = JSON.parse(storedFocus)
    } catch (_error) {
      return
    }

    if (!focusState || !focusState.name) return

    const input = Array.from(this.element.querySelectorAll("input, select, textarea")).find((field) => field.name === focusState.name)
    if (!input) return

    requestAnimationFrame(() => {
      this.focusInput(input)

      if (typeof input.setSelectionRange !== "function") return

      const selectionStart = focusState.selectionStart == null ? input.value.length : focusState.selectionStart
      const selectionEnd = focusState.selectionEnd == null ? input.value.length : focusState.selectionEnd
      const start = Math.min(selectionStart, input.value.length)
      const end = Math.min(selectionEnd, input.value.length)
      input.setSelectionRange(start, end)
    })
  }

  storeFocus(input) {
    if (!input || !input.name) return

    const focusState = {
      name: input.name,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd
    }

    sessionStorage.setItem(this.focusStorageKey, JSON.stringify(focusState))
  }

  clearStoredFocus() {
    sessionStorage.removeItem(this.focusStorageKey)
  }

  restoreScrollPosition() {
    const storedScrollPosition = sessionStorage.getItem(this.scrollStorageKey)
    if (!storedScrollPosition) return

    this.clearStoredScrollPosition()

    let scrollPosition
    try {
      scrollPosition = JSON.parse(storedScrollPosition)
    } catch (_error) {
      return
    }

    if (!scrollPosition) return

    requestAnimationFrame(() => {
      // Always restore the page-level scroll so the table stays in view.
      window.scrollTo(scrollPosition.x || 0, scrollPosition.y || 0)

      // When the table has a fixed-height scroll wrapper, also restore the
      // scroll position within that container (the wrapper's own scrollTop).
      if (this.hasScrollWrapperTarget && scrollPosition.wrapperTop != null) {
        this.scrollWrapperTarget.scrollTop = scrollPosition.wrapperTop
      }
    })
  }

  storeScrollPosition() {
    const scrollPosition = {
      x: window.pageXOffset || window.scrollX || 0,
      y: window.pageYOffset || window.scrollY || 0
    }

    // When the table has a fixed-height scroll wrapper, also capture its
    // internal scroll offset so we can restore it after a filter submit.
    if (this.hasScrollWrapperTarget) {
      scrollPosition.wrapperTop = this.scrollWrapperTarget.scrollTop
    }

    sessionStorage.setItem(this.scrollStorageKey, JSON.stringify(scrollPosition))
  }

  clearStoredScrollPosition() {
    sessionStorage.removeItem(this.scrollStorageKey)
  }

  focusInput(input) {
    try {
      input.focus({ preventScroll: true })
    } catch (_error) {
      input.focus()
    }
  }

  get focusStorageKey() {
    return `data-table-filters:${this.formIdValue}:focus`
  }

  get scrollStorageKey() {
    return `data-table-filters:${this.formIdValue}:scroll`
  }

  // ── Debug instrumentation ────────────────────────────────────────────────
  //
  // Enable from the browser console (then reload):
  //   localStorage.setItem('dt:debug', '1')
  //
  // Disable:
  //   localStorage.removeItem('dt:debug')
  //
  // Each instrumented step logs its duration with colour-coding:
  //   grey  < 5 ms   (fast, expected)
  //   orange  5–16 ms  (slow but sub-frame)
  //   red   > 16 ms  (janks a 60 fps frame — investigate)
  //
  // The PerformanceLongTaskObserver also fires automatically for any main-
  // thread block > 50 ms, even from code we haven't wrapped with _timed().
  // ────────────────────────────────────────────────────────────────────────

  get _debugEnabled() {
    return localStorage.getItem("dt:debug") === "1"
  }

  // Wrap fn() in a performance.mark/measure pair and log the duration.
  // Has zero overhead when debug mode is off.
  _timed(label, fn) {
    if (!this._debugEnabled) return fn()

    const startMark = `dt:${label}:start`
    const endMark = `dt:${label}:end`
    performance.mark(startMark)
    const result = fn()
    performance.mark(endMark)
    performance.measure(label, startMark, endMark)

    const entries = performance.getEntriesByName(label, "measure")
    const duration = entries[entries.length - 1]?.duration ?? 0
    const style =
      duration > 16 ? "color: red; font-weight: bold"
        : duration > 5 ? "color: orange"
          : "color: gray"
    console.debug(`%c[DataTable] ${label}: ${duration.toFixed(2)}ms`, style)

    return result
  }

  // Attach a PerformanceObserver for longtasks so we catch any main-thread
  // block > 50 ms automatically, including browser internals and third-party
  // code that _timed() doesn't cover.
  _observeLongTasks() {
    if (!this._debugEnabled || !window.PerformanceObserver) return

    try {
      this._longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          console.warn(
            `[DataTable] ⚠️ Long task: ${entry.duration.toFixed(0)}ms` +
            ` (start: ${entry.startTime.toFixed(0)}ms)`,
            entry
          )
        }
      })
      this._longTaskObserver.observe({ entryTypes: ["longtask"] })
    } catch (_e) {
      // "longtask" is not supported in every browser (e.g. Firefox/Safari);
      // safe to ignore.
    }
  }
}
